import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack, Avatar,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert,
} from '@mui/material';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadSoc, addSocShift } from './socSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const SEV_COLOR = { LOW: tokens.severity.low, MEDIUM: tokens.severity.medium, HIGH: tokens.severity.high, CRITICAL: tokens.severity.critical };
const MODULE_LABEL = { incident: 'Incident', ticket: 'Ticket', 'threat-monitoring': 'Threat Event', vulnerability: 'Vulnerability' };
const SHIFT_ROLES = ['TIER_1', 'TIER_2', 'TIER_3', 'LEAD'];

const emptyShift = { analystUserId: '', startsAt: '', endsAt: '', role: 'TIER_1', notes: '' };

export default function SocPage() {
  const dispatch = useDispatch();
  const { queue, shifts, onDuty, users } = useSelector((s) => s.soc);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyShift);
  const [error, setError] = useState(null);

  useEffect(() => { dispatch(loadSoc()); }, [dispatch]);

  // Shift responses only carry analystUserId (a UUID) — there is no
  // resolved display name on that record, so it's looked up here from the
  // users list fetched alongside SOC data.
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const analystName = (id) => usersById.get(id)?.name || 'Unassigned';

  const critical = queue.filter((q) => q.severity === 'CRITICAL').length;

  const handleCreateShift = async () => {
    setError(null);
    if (!form.analystUserId || !form.startsAt || !form.endsAt) return setError('Analyst, start time, and end time are all required.');
    const result = await dispatch(addSocShift({
      analystUserId: form.analystUserId,
      startsAt: new Date(form.startsAt).toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      role: form.role,
      notes: form.notes,
    }));
    if (addSocShift.rejected.match(result)) {
      setError(result.error?.message || 'Could not create this shift.');
      return;
    }
    setDialogOpen(false);
    setForm(emptyShift);
  };

  return (
    <Box>
      <PageHeader
        title="SOC"
        subtitle="One prioritized worklist across Incidents, Tickets, Threat Monitoring, and Vulnerability Management"
        action={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>Schedule Shift</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Queue Items" value={queue.length} icon={HeadsetMicRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Critical" value={critical} accent={tokens.severity.critical} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="On Duty Now" value={onDuty.length} accent={tokens.severity.low} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Unified Queue</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Module', 'Title', 'Severity', 'Status', 'When'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {queue.map((q) => (
                  <TableRow key={`${q.sourceModule}-${q.id}`} hover>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{MODULE_LABEL[q.sourceModule]}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', maxWidth: 260 }}>{q.title}</TableCell>
                    <TableCell><Chip label={q.severity} size="small" sx={{ color: SEV_COLOR[q.severity], border: `1px solid ${SEV_COLOR[q.severity]}`, backgroundColor: 'transparent' }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.76rem' }}>{q.status.replace('_', ' ')}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(q.createdAt)}</TableCell>
                  </TableRow>
                ))}
                {!queue.length && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>The queue is empty.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>On Duty Now</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {onDuty.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: tokens.brand.dim, fontSize: '0.8rem' }}>{analystName(s.analystUserId).charAt(0)}</Avatar>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary }}>{analystName(s.analystUserId)}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>{(s.role || '').replace(/_/g, ' ')}</Typography>
                  </Box>
                </Box>
              ))}
              {!onDuty.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted }}>No one is on shift right now.</Typography>}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Upcoming Shifts</Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {shifts.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: tokens.text.secondary }}>{analystName(s.analystUserId)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{formatRelativeTime(s.startsAt)}</Typography>
                </Box>
              ))}
              {!shifts.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted }}>No shifts scheduled yet.</Typography>}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule SOC Shift</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Analyst" size="small" value={form.analystUserId} onChange={(e) => setForm((f) => ({ ...f, analystUserId: e.target.value }))}>
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
            <TextField select label="Role" size="small" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              {SHIFT_ROLES.map((r) => <MenuItem key={r} value={r}>{r.replaceAll('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField label="Starts At" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} />
            <TextField label="Ends At" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} />
            <TextField label="Notes" size="small" multiline minRows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateShift}>Schedule Shift</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
